import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useClaimsStore } from '@/stores/claims-store';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { LocationPicker } from '@/components/claims/location-picker';
import { AddressPicker } from '@/components/claims/address-picker';
import { api, ApiError } from '@/lib/api';
import { clsx } from 'clsx';
import { customerPrefixes, ukPostcodeSchema } from '@fixtyer/shared';
import type { DamageType, CustomerPrefix } from '@fixtyer/shared';
import {
  trackClaimCreated,
  trackWizardStarted,
  trackWizardStepCompleted,
  type WizardStep,
} from '@/lib/analytics';
import {
  clearGuestDraft,
  guestDraftToClaimDefaults,
  loadGuestDraft,
} from '@/lib/guest-draft';

const steps = [
  { id: 'customer', title: 'Your Details' },
  { id: 'incident', title: 'Incident Details' },
  { id: 'location', title: 'Location' },
  { id: 'damage', title: 'Damage Details' },
  { id: 'vehicle', title: 'Vehicle' },
];

const customerSchema = z.object({
  customerPrefix: z.enum(customerPrefixes, { required_error: 'Please select a title' }),
  customerForename: z.string().min(1, 'First name is required'),
  customerSurname: z.string().min(1, 'Last name is required'),
  customerAddressLine1: z.string().min(1, 'Address is required'),
  customerAddressLine2: z.string().optional(),
  customerCity: z.string().min(1, 'Town/City is required'),
  customerPostcode: ukPostcodeSchema,
  customerPlaceId: z.string().optional(),
});

const incidentSchema = z.object({
  incidentDate: z.string().min(1, 'Date is required'),
  incidentTime: z.string().min(1, 'Time is required'),
});

const locationSchema = z.object({
  locationAddress: z.string().min(5, 'Address is required'),
  locationPostcode: ukPostcodeSchema,
  locationLat: z.number().optional(),
  locationLng: z.number().optional(),
  locationWhat3Words: z.string().optional(),
});

const damageSchema = z.object({
  damageType: z.array(z.string()).min(1, 'Select at least one damage type'),
  damageDescription: z.string().min(10, 'Please describe the damage'),
  repairCost: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (!value || value.trim() === '') return true;
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed >= 0;
      },
      { message: 'Enter a valid repair cost (0 or more)' }
    ),
});

const vehicleSchema = z.object({
  vehicleReg: z.string().min(1, 'Registration is required'),
  vehicleMake: z.string().optional(), // Populated from lookup or manual entry
  vehicleModel: z.string().optional(), // Populated from lookup or manual entry
  vehicleYear: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (!value || value.trim() === '') return true;
        const parsed = Number(value);
        return Number.isInteger(parsed) && parsed >= 1900 && parsed <= new Date().getFullYear() + 1;
      },
      { message: 'Enter a valid year' }
    ),
});

type CustomerFormData = z.infer<typeof customerSchema>;
type IncidentFormData = z.infer<typeof incidentSchema>;
type LocationFormData = z.infer<typeof locationSchema>;
type DamageFormData = z.infer<typeof damageSchema>;
type VehicleFormData = z.infer<typeof vehicleSchema>;
type AllFormData = CustomerFormData & IncidentFormData & LocationFormData & DamageFormData & VehicleFormData;

const prefixOptions = [
  { value: 'Mr', label: 'Mr' },
  { value: 'Mrs', label: 'Mrs' },
  { value: 'Miss', label: 'Miss' },
  { value: 'Mx', label: 'Mx' },
];

const damageTypes = [
  { value: 'tyre', label: 'Tyre' },
  { value: 'wheel', label: 'Wheel/Alloy' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'bodywork', label: 'Bodywork' },
  { value: 'steering', label: 'Steering' },
  { value: 'other', label: 'Other' },
];

export function NewClaimPage() {
  const navigate = useNavigate();
  const { createClaim } = useClaimsStore();
  const [guestDraft] = useState(() => loadGuestDraft());
  const guestDefaults = guestDraft ? guestDraftToClaimDefaults(guestDraft) : null;
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<AllFormData>>({});
  const startedTracked = useRef(false);

  useEffect(() => {
    if (startedTracked.current) return;
    startedTracked.current = true;
    trackWizardStarted();
  }, []);

  // Customer address state (managed outside react-hook-form for Google Places integration)
  const [customerAddressLine1, setCustomerAddressLine1] = useState('');
  const [customerAddressLine2, setCustomerAddressLine2] = useState('');
  const [customerCity, setCustomerCity] = useState('');
  const [customerPostcode, setCustomerPostcode] = useState('');
  const [customerPlaceId, setCustomerPlaceId] = useState<string | undefined>();

  // Location state (managed outside react-hook-form for map integration)
  const [locationAddress, setLocationAddress] = useState('');
  const [locationPostcode, setLocationPostcode] = useState(
    guestDefaults?.locationPostcode ?? ''
  );
  const [locationLat, setLocationLat] = useState<number | undefined>();
  const [locationLng, setLocationLng] = useState<number | undefined>();
  const [locationWhat3Words, setLocationWhat3Words] = useState<string | undefined>();
  const [locationPinError, setLocationPinError] = useState<string | null>(null);

  // Vehicle lookup state
  const [isLookingUpVehicle, setIsLookingUpVehicle] = useState(false);
  const [vehicleLookupError, setVehicleLookupError] = useState<string | null>(null);
  const [manualVehicleEntry, setManualVehicleEntry] = useState(false);
  const [vehicleLookupData, setVehicleLookupData] = useState<Record<string, unknown> | null>(null);
  const [vehicleDetails, setVehicleDetails] = useState<{
    make?: string;
    model?: string;
    colour?: string;
    year?: number;
    fuelType?: string;
    engineCapacity?: number;
    transmission?: string;
    gearCount?: number;
    driveType?: string;
    bodyStyle?: string;
    doors?: number;
    seats?: number;
    euroNcapRating?: number;
    fuelEconomyMpg?: number;
  } | null>(null);

  const customerForm = useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      customerPrefix: undefined,
      customerForename: '',
      customerSurname: '',
      customerAddressLine1: '',
      customerAddressLine2: '',
      customerCity: '',
      customerPostcode: '',
      customerPlaceId: '',
    },
  });

  const incidentForm = useForm<IncidentFormData>({
    resolver: zodResolver(incidentSchema),
    defaultValues: {
      incidentDate: guestDefaults?.incidentDate ?? '',
      incidentTime: '',
    },
  });

  const locationForm = useForm<LocationFormData>({
    resolver: zodResolver(locationSchema),
    defaultValues: {
      locationAddress: '',
      locationPostcode: guestDefaults?.locationPostcode ?? '',
    },
  });

  const damageForm = useForm<DamageFormData>({
    resolver: zodResolver(damageSchema),
    defaultValues: {
      damageType: guestDefaults?.damageType ?? [],
      damageDescription: '',
      repairCost: '',
    },
  });

  const vehicleForm = useForm<VehicleFormData>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: {
      vehicleReg: '',
      vehicleMake: '',
      vehicleModel: '',
      vehicleYear: '',
    },
  });

  // Vehicle registration lookup
  const handleVehicleLookup = async () => {
    const reg = vehicleForm.getValues('vehicleReg');
    if (!reg || reg.length < 2) {
      setVehicleLookupError('Please enter a registration number');
      return;
    }

    setIsLookingUpVehicle(true);
    setVehicleLookupError(null);
    setVehicleDetails(null);
    setVehicleLookupData(null);

    try {
      // Authenticated request via the shared api client (handles token refresh)
      const data = await api.post<Record<string, any>>('/api/vehicles/lookup', {
        registration: reg,
      });

      if (!data.success) {
        setVehicleLookupError(data.error || 'Vehicle lookup failed');
        return;
      }

      // Auto-fill make and model
      if (data.make) {
        vehicleForm.setValue('vehicleMake', data.make);
      }
      if (data.model) {
        vehicleForm.setValue('vehicleModel', data.model);
      }

      // Store all details for display and submission
      setVehicleDetails({
        make: data.make,
        model: data.model,
        colour: data.colour,
        year: data.year,
        fuelType: data.fuelType,
        engineCapacity: data.engineCapacity,
        transmission: data.transmission,
        gearCount: data.gearCount,
        driveType: data.driveType,
        bodyStyle: data.bodyStyle,
        doors: data.doors,
        seats: data.seats,
        euroNcapRating: data.euroNcapRating,
        fuelEconomyMpg: data.fuelEconomyMpg,
      });
      
      // Store raw lookup data for the database
      setVehicleLookupData(data.raw || data);
      setManualVehicleEntry(false);
    } catch (error) {
      setVehicleLookupError(
        error instanceof ApiError
          ? error.message
          : 'Failed to lookup vehicle. Please enter details manually.'
      );
    } finally {
      setIsLookingUpVehicle(false);
    }
  };

  const handleNext = async () => {
    let isValid = false;
    let values: Partial<AllFormData> = {};

    switch (currentStep) {
      case 0:
        // Validate customer details with values from state
        customerForm.setValue('customerAddressLine1', customerAddressLine1);
        customerForm.setValue('customerAddressLine2', customerAddressLine2);
        customerForm.setValue('customerCity', customerCity);
        customerForm.setValue('customerPostcode', customerPostcode);
        customerForm.setValue('customerPlaceId', customerPlaceId || '');
        isValid = await customerForm.trigger();
        values = {
          ...customerForm.getValues(),
          customerAddressLine1,
          customerAddressLine2,
          customerCity,
          customerPostcode,
          customerPlaceId,
        };
        break;
      case 1:
        isValid = await incidentForm.trigger();
        values = incidentForm.getValues();
        break;
      case 2: {
        // Validate location with values from state
        locationForm.setValue('locationAddress', locationAddress);
        locationForm.setValue('locationPostcode', locationPostcode);
        isValid = await locationForm.trigger();
        
        // The map pin is required: it's how we identify the responsible authority
        if (locationLat == null || locationLng == null) {
          setLocationPinError('Please pin the exact pothole location on the map before continuing.');
          isValid = false;
        } else {
          setLocationPinError(null);
        }
        
        // Fetch what3words only when clicking Continue (to save API calls)
        let w3w = locationWhat3Words;
        if (isValid && locationLat && locationLng && !locationWhat3Words) {
          try {
            const data = await api.get<{ success: boolean; words?: string }>(
              `/api/location/what3words?lat=${locationLat}&lng=${locationLng}`
            );
            if (data.success && data.words) {
              w3w = `///${data.words}`;
              setLocationWhat3Words(w3w);
            }
          } catch (error) {
            console.error('What3Words lookup failed:', error);
          }
        }
        
        values = {
          locationAddress,
          locationPostcode,
          locationLat,
          locationLng,
          locationWhat3Words: w3w,
        };
        break;
      }
      case 3:
        isValid = await damageForm.trigger();
        values = damageForm.getValues();
        break;
      case 4:
        isValid = await vehicleForm.trigger();
        if (isValid && manualVehicleEntry) {
          // Manual entry: make and model are required (year is optional)
          const { vehicleMake, vehicleModel } = vehicleForm.getValues();
          if (!vehicleMake?.trim()) {
            vehicleForm.setError('vehicleMake', { message: 'Make is required' });
            isValid = false;
          }
          if (!vehicleModel?.trim()) {
            vehicleForm.setError('vehicleModel', { message: 'Model is required' });
            isValid = false;
          }
        } else if (isValid && !vehicleDetails) {
          // Require either a successful lookup or manual entry
          setVehicleLookupError('Please look up your vehicle, or enter the details manually');
          isValid = false;
        }
        values = vehicleForm.getValues();
        break;
    }

    if (!isValid) return;

    trackWizardStepCompleted(steps[currentStep]!.id as WizardStep);
    setFormData((prev) => ({ ...prev, ...values }));

    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    const allData: AllFormData = {
      ...formData,
      ...vehicleForm.getValues(),
    } as AllFormData;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const claim = await createClaim({
        // Customer details
        customerPrefix: allData.customerPrefix as CustomerPrefix,
        customerForename: allData.customerForename,
        customerSurname: allData.customerSurname,
        customerAddressLine1: allData.customerAddressLine1,
        customerAddressLine2: allData.customerAddressLine2 || undefined,
        customerCity: allData.customerCity,
        customerPostcode: allData.customerPostcode,
        customerPlaceId: allData.customerPlaceId || undefined,
        // Incident details
        incidentDate: allData.incidentDate,
        incidentTime: allData.incidentTime || undefined,
        locationAddress: allData.locationAddress,
        locationPostcode: allData.locationPostcode,
        locationLat: allData.locationLat,
        locationLng: allData.locationLng,
        locationWhat3Words: allData.locationWhat3Words,
        damageType: allData.damageType as DamageType[],
        damageDescription: allData.damageDescription,
        repairCost: allData.repairCost ? parseFloat(allData.repairCost) : undefined,
        vehicleReg: allData.vehicleReg,
        vehicleMake: allData.vehicleMake,
        vehicleModel: allData.vehicleModel,
        // Vehicle lookup data (or manually entered year)
        vehicleYear:
          vehicleDetails?.year ??
          (allData.vehicleYear ? parseInt(allData.vehicleYear, 10) : undefined),
        vehicleColour: vehicleDetails?.colour,
        vehicleFuelType: vehicleDetails?.fuelType,
        vehicleEngineCC: vehicleDetails?.engineCapacity,
        vehicleLookupData: vehicleLookupData || undefined,
      });
      trackClaimCreated();
      clearGuestDraft();
      navigate(`/claims/${claim.id}`);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Something went wrong creating your claim. Please try again.'
      );
      setIsSubmitting(false);
    }
  };

  const selectedDamageTypes = damageForm.watch('damageType') || [];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-brand-950">
          New Claim
        </h1>
        <p className="mt-2 text-surface-800">
          Tell us about the incident and the damage caused
        </p>
        {guestDraft && (
          <p
            role="status"
            className="mt-4 rounded-xl border border-brand-200 bg-brand-50 p-3 text-sm text-brand-900"
          >
            Your preliminary details were restored. Review them as you complete each
            required step.
          </p>
        )}
      </div>

      {/* Progress steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div
                className={clsx(
                  'flex items-center justify-center w-10 h-10 rounded-full font-semibold text-sm transition-colors',
                  index < currentStep
                    ? 'bg-brand-600 text-white'
                    : index === currentStep
                    ? 'bg-brand-600 text-white ring-4 ring-brand-100'
                    : 'bg-surface-100 text-surface-800'
                )}
              >
                {index < currentStep ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  index + 1
                )}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={clsx(
                    'hidden sm:block w-20 lg:w-32 h-1 mx-2',
                    index < currentStep ? 'bg-brand-600' : 'bg-surface-200'
                  )}
                />
              )}
            </div>
          ))}
        </div>
        <div className="hidden sm:flex justify-between mt-2">
          {steps.map((step) => (
            <span key={step.id} className="text-sm text-surface-800">
              {step.title}
            </span>
          ))}
        </div>
      </div>

      {/* Form content */}
      <div className="bg-white rounded-2xl border border-surface-200 p-8">
        <h2 className="font-display text-xl font-semibold text-brand-950 mb-6">
          {steps[currentStep]?.title}
        </h2>

        {/* Step 1: Customer Details */}
        {currentStep === 0 && (
          <div className="space-y-6">
            <p className="text-sm text-surface-600 mb-4">
              Please provide your details as they will appear on your claim.
            </p>
            
            {/* Name row */}
            <div className="grid sm:grid-cols-3 gap-4">
              <Select
                label="Title"
                options={prefixOptions}
                placeholder="Select..."
                error={customerForm.formState.errors.customerPrefix?.message}
                {...customerForm.register('customerPrefix')}
              />
              <Input
                label="First name"
                placeholder="e.g., John"
                error={customerForm.formState.errors.customerForename?.message}
                {...customerForm.register('customerForename')}
              />
              <Input
                label="Last name"
                placeholder="e.g., Smith"
                error={customerForm.formState.errors.customerSurname?.message}
                {...customerForm.register('customerSurname')}
              />
            </div>

            {/* Address with Google lookup */}
            <div className="pt-4 border-t border-surface-100">
              <h3 className="text-sm font-medium text-brand-950 mb-4">Your Address</h3>
              <AddressPicker
                addressLine1={customerAddressLine1}
                addressLine2={customerAddressLine2}
                city={customerCity}
                postcode={customerPostcode}
                onAddressChange={(data) => {
                  if (data.addressLine1 !== undefined) {
                    setCustomerAddressLine1(data.addressLine1);
                    customerForm.setValue('customerAddressLine1', data.addressLine1);
                  }
                  if (data.addressLine2 !== undefined) {
                    setCustomerAddressLine2(data.addressLine2);
                    customerForm.setValue('customerAddressLine2', data.addressLine2);
                  }
                  if (data.city !== undefined) {
                    setCustomerCity(data.city);
                    customerForm.setValue('customerCity', data.city);
                  }
                  if (data.postcode !== undefined) {
                    setCustomerPostcode(data.postcode);
                    customerForm.setValue('customerPostcode', data.postcode);
                  }
                  if (data.placeId !== undefined) {
                    setCustomerPlaceId(data.placeId);
                    customerForm.setValue('customerPlaceId', data.placeId);
                  }
                }}
                error={customerForm.formState.errors.customerAddressLine1?.message}
              />
              {/* Show city/postcode validation errors */}
              {customerForm.formState.errors.customerCity && (
                <p className="mt-2 text-sm text-error-600">
                  {customerForm.formState.errors.customerCity.message}
                </p>
              )}
              {customerForm.formState.errors.customerPostcode && (
                <p className="mt-2 text-sm text-error-600">
                  {customerForm.formState.errors.customerPostcode.message}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Incident Details */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <Input
              label="When did the incident happen?"
              type="date"
              max={new Date().toISOString().split('T')[0]}
              error={incidentForm.formState.errors.incidentDate?.message}
              {...incidentForm.register('incidentDate')}
            />
            <Input
              label="What time?"
              type="time"
              error={incidentForm.formState.errors.incidentTime?.message}
              {...incidentForm.register('incidentTime')}
            />
          </div>
        )}

        {/* Step 3: Location */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <LocationPicker
              address={locationAddress}
              postcode={locationPostcode}
              onAddressChange={(addr) => {
                setLocationAddress(addr);
                locationForm.setValue('locationAddress', addr);
              }}
              onPostcodeChange={(pc) => {
                setLocationPostcode(pc);
                locationForm.setValue('locationPostcode', pc);
              }}
              onLocationChange={(lat, lng) => {
                setLocationLat(lat);
                setLocationLng(lng);
                setLocationPinError(null);
                // Clear what3words when location changes - will fetch on Continue
                setLocationWhat3Words(undefined);
              }}
            />
            
            {/* Validation errors */}
            {locationForm.formState.errors.locationAddress && (
              <p className="text-sm text-error-600">
                {locationForm.formState.errors.locationAddress.message}
              </p>
            )}
            {locationForm.formState.errors.locationPostcode && (
              <p className="text-sm text-error-600">
                {locationForm.formState.errors.locationPostcode.message}
              </p>
            )}
            {locationPinError && (
              <p className="text-sm text-error-600">{locationPinError}</p>
            )}

            {/* Location confirmation */}
            {locationLat && locationLng && (
              <div className="p-4 bg-success-500/10 border border-success-500/20 rounded-xl">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-sm font-medium text-success-700">
                    Location pinpointed on map
                  </p>
                </div>
                <p className="mt-1 text-xs text-success-600">
                  This helps us identify the responsible highway authority
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Damage Details */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-brand-950 mb-3">
                What was damaged?
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {damageTypes.map((type) => (
                  <label
                    key={type.value}
                    className={clsx(
                      'flex items-center justify-center px-4 py-3 rounded-xl border-2 cursor-pointer transition-colors',
                      selectedDamageTypes.includes(type.value)
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-surface-200 hover:border-surface-300'
                    )}
                  >
                    <input
                      type="checkbox"
                      value={type.value}
                      className="sr-only"
                      {...damageForm.register('damageType')}
                    />
                    {type.label}
                  </label>
                ))}
              </div>
              {damageForm.formState.errors.damageType && (
                <p className="mt-2 text-sm text-error-600">
                  {damageForm.formState.errors.damageType.message}
                </p>
              )}
            </div>
            <div>
              <Textarea
                label="Describe the damage"
                placeholder="e.g., Front nearside tyre punctured, alloy wheel cracked"
                rows={4}
                error={damageForm.formState.errors.damageDescription?.message}
                {...damageForm.register('damageDescription')}
              />
              <div className="mt-2 p-3 bg-surface-50 rounded-lg border border-surface-200">
                <p className="text-xs font-medium text-surface-700 mb-1">💡 What to include:</p>
                <ul className="text-xs text-surface-600 space-y-0.5">
                  <li>• What happened — e.g., "hit a pothole", "drove over a defect"</li>
                  <li>• What damage occurred — e.g., "tyre blew out", "wheel cracked"</li>
                  <li>• When you noticed — immediately, or after pulling over</li>
                  <li>• Any sounds or sensations — e.g., "loud bang", "steering pulled left"</li>
                </ul>
              </div>
            </div>
            <Input
              label="Repair cost (£) - if known"
              type="number"
              step="0.01"
              placeholder="e.g., 385.00"
              error={damageForm.formState.errors.repairCost?.message}
              {...damageForm.register('repairCost')}
            />
          </div>
        )}

        {/* Step 5: Vehicle */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Input
                  label="Vehicle registration"
                  placeholder="e.g., AB12 CDE"
                  error={vehicleForm.formState.errors.vehicleReg?.message}
                  {...vehicleForm.register('vehicleReg')}
                />
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={handleVehicleLookup}
                loading={isLookingUpVehicle}
                className="mb-0.5"
              >
                {isLookingUpVehicle ? 'Looking up...' : 'Lookup'}
              </Button>
            </div>
            
            {vehicleLookupError && (
              <div className="-mt-4 space-y-2">
                <p className="text-sm text-error-600">{vehicleLookupError}</p>
                {!manualVehicleEntry && (
                  <button
                    type="button"
                    onClick={() => {
                      setManualVehicleEntry(true);
                      setVehicleLookupError(null);
                      setVehicleDetails(null);
                      setVehicleLookupData(null);
                    }}
                    className="text-sm font-medium text-brand-600 hover:text-brand-700 underline"
                  >
                    Enter vehicle details manually instead
                  </button>
                )}
              </div>
            )}

            {/* Manual vehicle entry - fallback when the lookup fails */}
            {manualVehicleEntry && (
              <div className="p-5 bg-surface-50 border border-surface-200 rounded-xl space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-brand-950">
                    Enter your vehicle details
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setManualVehicleEntry(false);
                      vehicleForm.clearErrors(['vehicleMake', 'vehicleModel', 'vehicleYear']);
                    }}
                    className="text-xs text-brand-600 hover:text-brand-700 underline"
                  >
                    Try lookup again
                  </button>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Input
                    label="Make"
                    placeholder="e.g., Ford"
                    error={vehicleForm.formState.errors.vehicleMake?.message}
                    {...vehicleForm.register('vehicleMake')}
                  />
                  <Input
                    label="Model"
                    placeholder="e.g., Focus"
                    error={vehicleForm.formState.errors.vehicleModel?.message}
                    {...vehicleForm.register('vehicleModel')}
                  />
                  <Input
                    label="Year (optional)"
                    type="number"
                    placeholder="e.g., 2019"
                    error={vehicleForm.formState.errors.vehicleYear?.message}
                    {...vehicleForm.register('vehicleYear')}
                  />
                </div>
              </div>
            )}

            {/* Vehicle details found */}
            {vehicleDetails && (
              <div className="bg-success-50 border border-success-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-success-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold text-success-800">Vehicle Found</span>
                  {vehicleDetails.euroNcapRating && (
                    <span className="ml-auto flex items-center gap-1 text-sm text-amber-600">
                      {'★'.repeat(vehicleDetails.euroNcapRating)}{'☆'.repeat(5 - vehicleDetails.euroNcapRating)}
                      <span className="text-xs text-surface-500 ml-1">NCAP</span>
                    </span>
                  )}
                </div>
                
                {/* Main details - Make, Model, Body Style, Year */}
                <div className="bg-white rounded-lg p-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl font-bold text-brand-950">
                      {vehicleDetails.year} {vehicleDetails.make} {vehicleDetails.model}
                    </span>
                  </div>
                  {vehicleDetails.bodyStyle && (
                    <div className="text-sm text-surface-500 mt-0.5">{vehicleDetails.bodyStyle}</div>
                  )}
                  
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-sm text-surface-700">
                    {vehicleDetails.colour && (
                      <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-surface-400 border border-surface-300"></span>
                        {vehicleDetails.colour}
                      </span>
                    )}
                    {vehicleDetails.fuelType && (
                      <span>⛽ {vehicleDetails.fuelType}</span>
                    )}
                    {vehicleDetails.engineCapacity && (
                      <span>{(vehicleDetails.engineCapacity / 1000).toFixed(1)}L</span>
                    )}
                    {vehicleDetails.transmission && (
                      <span>
                        {vehicleDetails.transmission}
                        {vehicleDetails.gearCount && ` (${vehicleDetails.gearCount}-speed)`}
                      </span>
                    )}
                    {vehicleDetails.driveType && (
                      <span>{vehicleDetails.driveType}</span>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-surface-500">
                    {vehicleDetails.doors && (
                      <span>{vehicleDetails.doors} doors</span>
                    )}
                    {vehicleDetails.seats && (
                      <span>{vehicleDetails.seats} seats</span>
                    )}
                    {vehicleDetails.fuelEconomyMpg && (
                      <span>{vehicleDetails.fuelEconomyMpg} MPG</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Prompt to lookup if not done yet */}
            {!vehicleDetails && !vehicleLookupError && !manualVehicleEntry && (
              <div className="p-4 bg-brand-50 border border-brand-100 rounded-xl">
                <p className="text-sm text-brand-700">
                  👆 Enter your registration number and click <strong>Lookup</strong> to automatically fetch your vehicle details from DVLA records.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Submission error */}
        {submitError && (
          <div className="mt-8 p-4 bg-error-50 border border-error-200 rounded-xl" role="alert">
            <div className="flex items-start gap-3">
              <span className="text-error-500 text-xl">⚠️</span>
              <div>
                <p className="font-medium text-error-800">We couldn't create your claim</p>
                <p className="text-sm text-error-700 mt-1">{submitError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-surface-200">
          <Button
            type="button"
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            Back
          </Button>
          <Button
            type="button"
            onClick={handleNext}
            loading={isSubmitting}
          >
            {currentStep === steps.length - 1 ? 'Create Claim' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
}
